/**
 * @file: dark.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { PresentationTheme } from '@euler/app/tv/theme/types';

const theme: PresentationTheme = {
  page: {
    bg: 'rgba(19, 22, 30, 1)',
  },
  contentLoader: {
    fg: 'rgba(25, 32, 45, 1)',
    bg: 'rgba(29, 34, 46, 1)',
  },
  panel: {
    bg: 'rgba(29, 34, 46, 1)',
    titleTextColor: 'rgba(255, 255, 255, 0.6)',
    separatorStartColor: 'rgba(255, 255, 255, 0.2)',
    separatorEndColor: 'rgba(255, 255, 255, 0.01)',
    infoLabelColor: 'rgba(255, 255, 255, 0.5)',
    infoValueColor: 'rgba(255, 255, 255, 0.9)',
    listItemSeparatorColor: 'rgba(255, 255, 255, 0.03)',
    headerLinkTextColor: 'rgba(48, 117, 221, 1)',
  },
  statusBadge: {
    preInspection: {
      pending: 'rgba(239, 151, 19, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    inspection: {
      pending: 'rgba(239, 151, 19, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    construction: {
      pending: 'rgba(224, 105, 38, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    deliveryCheck: {
      pending: 'rgba(64, 195, 100, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
    },
    task: {
      pending: 'rgba(64, 195, 100, 1)',
      inprogress: 'rgba(48, 117, 221, 1)',
      finished: 'rgba(25, 202, 181, 1)',
      canceled: 'rgba(125, 138, 146, 1)',
    },
  },
  emptyPlaceholder: {
    textColor: 'rgba(255, 255, 255, 0.3)',
    iconColor: 'rgba(255, 255, 255, 0.5)',
    errorTextColor: 'rgba(255, 255, 255, 0.3)',
    iconImage: require('@euler/assets/img/empty-placeholder-dark.png'),
  },
  memberInfo: {
    primaryBgColor: 'rgba(40, 49, 63, 0.3)',
    secondaryBgColor: 'rgba(38, 65, 134, 0.3)',
    nameTextColor: 'rgba(255, 255, 255, 1)',
    jobTitleTextColor: 'rgba(255, 255, 255, 0.3)',
    detail: {
      bodyTextColor: 'rgba(255, 255, 255, 0.6)',
      quoteBgColor: 'rgba(40, 49, 63, 1)',
      quoteBorderColor: 'rgba(38, 65, 134, 0.3)',
    },
  },
  lpn: {
    input: {
      borderColor: 'rgba(255, 255, 255, 0.4)',
      activeBorderColor: 'rgba(48, 117, 221, 1)',
      textColor: 'rgba(255, 255, 255, 1)',
    },
    keyboard: {
      btnBgColor: 'rgba(255, 255, 255, 0.07)',
      btnTextColor: 'rgba(255, 255, 255, 0.8)',
      doneBtnBgColor: 'rgba(48, 117, 221, 1)',
    },
  },

  search: {
    criterion: {
      backButtonTextColor: 'rgba(255, 255, 255, 0.5)',
      optionLabelTextColor: 'rgba(255, 255, 255, 0.4)',
      optionValueTextColor: 'rgba(255, 255, 255, 0.6)',
      optionBorderColor: 'rgba(255, 255, 255, 0.05)',
      optionArrowColor: 'rgba(255, 255, 255, 0.3)',
      optionListItem: {
        titleTextColor: 'rgba(255, 255, 255, 0.9)',
        checkmarkIconColor: 'rgba(255, 255, 255, 0.5)',
        separatorColor: 'rgba(255, 255, 255, 0.1)',
      },
    },
    bookmark: {
      iconColor: 'rgba(239, 151, 19, 1)',
      titleTextColor: 'rgba(255, 255, 255, 0.9)',
      tagBorderColor: 'rgba(255, 255, 255, 0.3)',
      tagTextColor: 'rgba(255, 255, 255, 0.6)',
      separatorLineColor: 'rgba(255, 255, 255, 0.1)',
    },
    separatorLineColor: 'rgba(255, 255, 255, 0.03)',
  },

  modal: {
    bgColor: 'rgba(40, 49, 63, 0.7)',
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },

  toast: {
    bgColor: '#222222',
    iconColor: '#ffffff',
    textColor: '#ffffff',
  },

  alert: {
    backdropColor: 'rgba(0, 0, 0, 0.85)',
    bgColor: 'rgba(40, 49, 63, 0.7)',
    titleTextColor: 'rgba(255, 255, 255, 0.9)',
    messageTextColor: 'rgba(255, 255, 255, 0.5)',
    defaultButtonTextColor: 'rgba(255, 255, 255, 0.9)',
    textInputBgColor: 'rgba(0, 0, 0, 0.4)',
    textInputBorderColor: 'rgba(0, 0, 0, 0.4)',
    textInputTextColor: 'rgba(255, 255, 255, 0.9)',
    textInputPlaceholderTextColor: 'rgba(255, 255, 255, 0.4)',
    errorTextColor: 'rgba(246, 60, 104, 1)',
    buttons: {
      default: {
        textColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(255, 255, 255, 0.4)',
      },
      destructive: {
        textColor: 'rgba(246, 60, 104, 1)',
        borderColor: 'rgba(246, 60, 104, 1)',
      },
      cancel: {
        textColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(255, 255, 255, 0.4)',
      },
      confirm: {
        bgColor: ['rgba(58, 226, 206, 1)', 'rgba(48, 121, 231, 1)'],
        textColor: 'rgba(255, 255, 255, 0.9)',
      },
    },
  },

  button: {
    startColor: 'rgba(58, 226, 206, 1)',
    endColor: 'rgba(48, 121, 231, 1)',
    textColor: 'rgba(255, 255, 255, 0.9)',
  },

  launchScreen: {
    bgColor: 'rgba(29, 34, 46, 1)',
    brandTitleTextColor: '#fff',
    footerTextColor: 'rgba(255, 255, 255, 0.8)',
    footerCopyrightTextColor: 'rgba(255, 255, 255, 0.5)',
    footerPoweredByTextColor: 'rgba(255, 255, 255, 0.35)',
    activityIndicatorColor: 'rgba(255, 255, 255, 0.2)',
  },

  navbar: {
    backButtonColor: 'rgba(255, 255, 255, 0.8)',
    titleTextColor: 'rgba(255, 255, 255, 0.8)',
    action: {
      bgColor: 'rgba(255, 255, 255, 0.03)',
      iconColor: 'rgba(255, 255, 255, 0.8)',
      textColor: 'rgba(255, 255, 255, 0.6)',
    },
  },

  auth: {
    mainPanelBgColor: 'rgba(29, 34, 46, 1)',
    sidePanelBgColor: 'rgba(19, 22, 30, 1)',
    sidePanelVideoBgColor: '#000000',
    sideTitleTextColor: 'rgba(255, 255, 255, 1)',
    sideSubTitleTextColor: 'rgba(255, 255, 255, 0.7)',
    loginHeaderTitleTextColor: '#009FD4',
    loginHeaderSubTitleTextColor: 'rgba(255, 255, 255, 0.5)',
    linkTextColor: '#009FD4',
    buttonBgColor: '#009FD4',
    buttonTextColor: '#fff',
    disabledButtonBgColor: '#CED1D9',
    disabledButtonTextColor: 'rgba(0, 0, 0, 0.5)',
    agreementLabelTextColor: 'rgba(255, 255, 255, 0.5)',
    agreementCheckBoxFillColor: '#009FD4',
    backButtonColor: 'rgba(255, 255, 255, 0.5)',
    avatarBgColor: '#fff',
    codeFieldBgColor: '#fff',
    codeFieldTextColor: '#009FD4',
    codeTipTextColor: 'rgba(255, 255, 255, 0.5)',
    codeTtlTextColor: 'rgba(255, 255, 255, 0.5)',
    textInput: {
      bgColor: '#fff',
      textColor: '#000',
      placeholderTextColor: 'rgba(0, 0, 0, 0.5)',
      tvFocusColor: '#ddd',
    },
    loginMethods: {
      titleTextColor: '#9498A2',
      separatorLineColor: '#CED1D9',
      iconBgColor: '#fff',
      iconColor: '#000000',
    },
  },

  settings: {
    versionTextColor: 'rgba(255, 255, 255, 0.15)',
    userInfo: {
      titleColor: 'rgba(255, 255, 255, 0.8)',
      roleBorderColor: 'rgba(255, 255, 255, 0.2)',
      roleTextColor: 'rgba(255, 255, 255, 0.5)',
    },
    appearance: {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderColor: 'rgba(255, 255, 255, 0.2)',
      darkModeColor: 'rgba(255, 255, 255, 0.9)',
      lightModeColor: 'rgba(239, 151, 19, 1)',
    },
    cell: {
      iconColor: 'rgba(255, 255, 255, 0.9)',
      textColor: 'rgba(255, 255, 255, 0.6)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  dashboard: {
    navbar: {
      bg: 'rgba(38, 65, 134, 1)',
      storeName: {
        color: 'rgba(255, 255, 255, 0.8)',
        icon: {
          color: 'rgba(25, 202, 181, 1)',
        },
      },
      searchInput: {
        textColor: 'rgba(255, 255, 255, 1)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        placeholderTextColor: 'rgba(255, 255, 255, 1)',
        icon: { color: 'rgba(255, 255, 255, 0.6)' },
      },
      icon: {
        inactiveColor: 'rgba(255, 255, 255, 0.4)',
        activeStartColor: 'rgba(81, 235, 207, 1)',
        activeEndColor: 'rgba(221, 239, 255, 1)',
      },
      exitPresentationMode: {
        textColor: 'rgba(255, 255, 255, 0.8)',
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
    },
    tab: {
      textColor: 'rgba(255, 255, 255, 0.6)',
      activeTextColor: 'rgba(255, 255, 255, 1)',
      activeEffect: {
        startColor: 'rgba(48, 173, 243, 1)',
        endColor: 'rgba(0, 0, 0, 0)',
      },
    },
    mode: {
      iconColor: 'rgba(255, 255, 255, 0.2)',
      activeIconColor: 'rgba(255, 255, 255, 0.8)',
    },
    card: {
      bg: 'rgba(29, 34, 46, 1)',
      licensePlateNoTextColor: 'rgba(255, 255, 255, 0.8)',
      vehicleNameTextColor: 'rgba(255, 255, 255, 0.5)',
      mileageTextColor: 'rgba(255, 255, 255, 0.8)',
      mileageBorderColor: 'rgba(139, 157, 218, 0.7)',
      separatorColor: 'rgba(56, 67, 91, 0.25)',
      labelColor: 'rgba(255, 255, 255, 0.4)',
      valueColor: 'rgba(255, 255, 255, 0.8)',
      activityIndicatorColor: 'rgba(255, 255, 255, 0.3)',
      observeStatus: {
        observedTextColor: 'rgba(103, 237, 140, 1)',
        unobservedTextColor: 'rgba(246, 221, 89, 1)',
        observedBorderColor: 'rgba(103, 237, 140, 1)',
        unobservedBorderColor: 'rgba(246, 221, 89, 1)',
      },
      icons: {
        serviceAgent: {
          color: 'rgba(255, 255, 255, 0.6)',
          startColor: 'rgba(253, 142, 106, 1)',
          endColor: 'rgba(128, 77, 235, 1)',
        },
        startTime: {
          color: 'rgba(255, 255, 255, 0.6)',
          startColor: 'rgba(91, 189, 243, 1)',
          endColor: 'rgba(102, 79, 241, 1)',
        },
        endTime: {
          color: 'rgba(255, 255, 255, 0.6)',
          startColor: 'rgba(246, 121, 67, 1)',
          endColor: 'rgba(246, 221, 89, 1)',
        },
        timeElapsed: {
          color: 'rgba(255, 255, 255, 0.6)',
          startColor: 'rgba(103, 237, 140, 1)',
          endColor: 'rgba(79, 169, 234, 1)',
        },
      },
    },
    contentLoader: {
      fg: 'rgba(25, 32, 45, 1)',
      bg: 'rgba(29, 34, 46, 1)',
    },
  },
  task: {
    navbar: {
      backButtonColor: 'rgba(255, 255, 255, 0.8)',
      licensePlateNoTextColor: 'rgba(255, 255, 255, 0.8)',
      action: {
        bgColor: 'rgba(255, 255, 255, 0.03)',
        iconColor: 'rgba(255, 255, 255, 0.8)',
        textColor: 'rgba(255, 255, 255, 0.6)',
      },
    },
    bottomBar: {
      bg: 'rgba(38, 65, 134, 1)',
      textColor: 'rgba(255, 255, 255, 0.6)',
      activeTextColor: 'rgba(255, 255, 255, 1)',
    },
    media: {
      titleColor: 'rgba(255, 255, 255, 0.6)',
      subTitleColor: 'rgba(255, 255, 255, 0.3)',
    },
    score: {
      labelTextColor: 'rgba(255, 255, 255, 0.8)',
      subLabelTextColor: 'rgba(255, 255, 255, 0.3)',
      outerArcColor: 'rgba(255, 255, 255, 0.1)',
      innerArcBgColor: 'rgba(255, 255, 255, 0.1)',
      innerArcColorStops: [
        [0, 'rgba(246, 60, 104, 1)'],
        [0.5, '#FF9900'],
        [0.75, '#F2D671'],
        [1, '#40C779'],
      ],
    },
    defectiveBadge: {
      textColor: 'rgba(0, 0, 0, 0.9)',
      bgColor: {
        urgent: 'rgba(244, 79, 99, 1)',
        warning: 'rgba(255, 138, 0, 1)',
        defective: 'rgba(254, 182, 74, 1)',
        normal: 'rgba(25, 202, 181, 1)',
      },
    },
    scrollRow: {
      separatorStartColor: 'rgba(255, 255, 255, 0.15)',
      separatorEndColor: 'rgba(255, 255, 255, 0.01)',
      head: {
        bg: 'rgba(45, 52, 69, 1)',
        titleTextColor: 'rgba(255, 255, 255, 0.8)',
        subTitleTextColor: 'rgba(255, 255, 255, 0.6)',
        labelTextColor: 'rgba(255, 255, 255, 0.6)',
        valueTextColor: 'rgba(255, 255, 255, 0.9)',
        statusTextColor: 'rgba(29, 34, 46, 1)',
      },
      user: {
        labelTextColor: 'rgba(255, 255, 255, 0.6)',
        valueTextColor: 'rgba(255, 255, 255, 0.9)',
        detailLinkTextColor: 'rgba(255, 255, 255, 0.3)',
      },
      media: {
        titleColor: 'rgba(255, 255, 255, 0.6)',
        subTitleColor: 'rgba(255, 255, 255, 0.3)',
      },
    },
    detail: {
      vehicleBanner: {
        vehicleNameTextColor: 'rgba(221, 221, 221, 1)',
        startColor: 'rgba(48, 117, 221, 1)',
        endColor: 'rgba(77, 208, 237, 1)',
        infoLabelColor: 'rgba(255, 255, 255, 0.6)',
        infoValueColor: 'rgba(255, 255, 255, 1)',
        separatorStartColor: 'rgba(255, 255, 255, 0.2)',
        separatorEndColor: 'rgba(255, 255, 255, 0.01)',
        observedStatusColor: 'rgba(255, 255, 255, 0.6)',
        unobservedStatusColor: 'rgba(255, 255, 255, 0.1)',
        starIconColor: 'rgba(239, 151, 19, 1)',
        outlineStarIconColor: 'rgba(255, 255, 255, 0.5)',
      },
      card: {
        bg: 'rgba(29, 34, 46, 1)',
        titleTextColor: 'rgba(255, 255, 255, 0.9)',
        titleSeparatorColor: 'rgba(255, 255, 255, 0.1)',
        infoLabelColor: 'rgba(255, 255, 255, 0.6)',
        infoValueColor: 'rgba(255, 255, 255, 0.9)',
        status: {
          bg: {
            pending: 'rgba(255, 255, 255, 0.1)',
            inprogress: 'rgba(48, 117, 221, 1)',
            finished: 'rgba(25, 202, 181, 1)',
          },
          fg: {
            pending: 'rgba(255, 255, 255, 0.4)',
            inprogress: 'rgba(255, 255, 255, 1)',
            finished: 'rgba(255, 255, 255, 1)',
          },
        },
      },
    },
    preInspection: {
      troubleLight: {
        textColor: 'rgba(255, 255, 255, 0.75)',
        badge: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
          bgColor: 'transparent',
          textColor: 'rgba(255, 255, 255, 0.5)',
          activeBorderColor: 'rgba(255, 255, 255, 0.2)',
          activeBgColor: 'rgba(25, 202, 181, 1)',
          activeTextColor: 'rgba(255, 255, 255, 1)',
        },
      },
    },
    inspection: {
      techInfo: {
        barColor: 'rgba(255, 255, 255, 0.5)',
        nameLabelTextColor: 'rgba(255, 255, 255, 1)',
        nameTextColor: 'rgba(255, 255, 255, 1)',
        timeTextColor: 'rgba(255, 255, 255, 0.6)',
        detailLinkTextColor: 'rgba(255, 255, 255, 0.3)',
      },
      defectiveChart: {
        labelColor: 'rgba(255, 255, 255, 0.6)',
        disabledLabelColor: 'rgba(255, 255, 255, 0.05)',
        iconColor: 'rgba(255, 255, 255, 0.6)',
        disabledIconColor: 'rgba(255, 255, 255, 0.05)',
        counterColor: 'rgba(242, 242, 242, 0.8)',
        disabledCounterColor: 'rgba(242, 242, 242, 0.05)',
        circleBgColor: 'rgba(255, 255, 255, 0.1)',
        defectiveColor: {
          urgent: 'rgba(244, 79, 99, 1)',
          warning: 'rgba(255, 138, 0, 1)',
          defective: 'rgba(254, 182, 74, 1)',
          normal: 'rgba(48, 117, 221, 1)',
        },
      },
      siteCard: {
        bg: 'rgba(29, 34, 46, 1)',
        titleTextColor: 'rgba(255, 255, 255, 0.8)',
        siteIconColor: 'rgba(255, 255, 255, 0.8)',
        separatorColor: 'rgba(255, 255, 255, 0.1)',
        labelTextColor: 'rgba(255, 255, 255, 0.3)',
        valueTextColor: 'rgba(255, 255, 255, 0.8)',
      },
    },
    construction: {
      sectionTitleTextColor: 'rgba(255, 255, 255, 0.6)',
      sectionCounterTextColor: 'rgba(255, 255, 255, 0.15)',
      unscheduledJob: {
        titleTextColor: 'rgba(255, 255, 255, 0.5)',
        labelTextColor: 'rgba(255, 255, 255, 0.2)',
        valueTextColor: 'rgba(255, 255, 255, 0.6)',
        valueRangeTextColor: 'rgba(255, 255, 255, 0.3)',
      },
      jobDetailPage: {
        media: {
          titleTextColor: 'rgba(255, 255, 255, 0.6)',
        },
      },
    },
    siteDetail: {
      titleTextColor: 'rgba(255, 255, 255, 0.8)',
      bgColor: 'rgba(40, 49, 63, 0.7)',
      separatorColor: 'rgba(255, 255, 255, 0.15)',
      media: {
        counterOverlayBgColor: 'rgba(29, 34, 46, 0.75)',
        counterOverlayTextColor: 'rgba(255, 255, 255, 0.5)',
        placeholderBgColor: 'rgba(29, 34, 46, 0.5)',
        placeholderIconColor: 'rgba(255, 255, 255, 0.15)',
        titleBgColor: 'rgba(13, 13, 13, 0.15)',
        titleTextColor: 'rgba(255, 255, 255, 0.8)',
      },
      item: {
        labelTextColor: 'rgba(255, 255, 255, 0.5)',
        valueTextColor: 'rgba(255, 255, 255, 0.9)',
        valueTipTextColor: 'rgba(255, 255, 255, 0.4)',
        valueRangeTextColor: 'rgba(255, 255, 255, 0.5)',
        valueRangeLabelTextColor: 'rgba(255, 255, 255, 0.3)',
        descTextColor: 'rgba(255, 255, 255, 0.4)',
      },
      advice: {
        labelTextColor: 'rgba(255, 255, 255, 0.8)',
        serviceItem: {
          bgColor: 'rgba(48, 117, 221, 1)',
          textColor: 'rgba(255, 255, 255, 1)',
        },
      },
    },
    maintenancePlan: {
      iconColor: 'rgba(255, 255, 255, 0.8)',
      iconBgColor: 'rgba(255, 255, 255, 0.15)',
      defaultDefectiveBorderColor: 'rgba(48, 117, 221, 1)',
      viewDetailTextColor: 'rgba(255, 255, 255, 0.45)',
      confirmedLabelTextColor: '#19CAB5',
      slider: {
        thumbStartColor: '#3075dd',
        thumbStopColor: '#2dcce1',
        thumbStartColorPending: '#FF9900',
        thumbStopColorPending: '#F2D671',
        thumbStartColorConfirmed: '#40C779',
        thumbStopColorConfirmed: '#19CAB5',
        thumbMileageTextColor: 'rgba(255, 255, 255, 1)',
        thumbTimeTextColor: 'rgba(255, 255, 255, 1)',
        thumbStatusTextColor: 'rgba(255, 255, 255, 0.5)',
        trackBgColor: 'rgba(19, 22, 30, 1)',
        tipTextColor: 'rgba(255, 255, 255, 0.15)',
        tickTextColor: 'rgba(255, 255, 255, 0.15)',
        tickColor: 'rgba(255, 255, 255, 0.15)',
      },
      custom: {
        tabTextColor: 'rgba(255, 255, 255, 0.4)',
        activeTabTextColor: 'rgba(255, 255, 255, 0.9)',
        tabUnderlineColor: 'rgba(48, 117, 221, 1)',
        textInputBgColor: 'rgba(0, 0, 0, 0.4)',
        textInputBorderColor: 'rgba(0, 0, 0, 0.4)',
        textInputUnitTextColor: 'rgba(255, 255, 255, 0.5)',
        textInputTextColor: 'rgba(255, 255, 255, 0.9)',
        pickerItemTextColor: 'rgba(255, 255, 255, 0.3)',
        activePickerItemTextColor: 'rgba(255, 255, 255, 0.9)',
      },
    },
    subscribeBindModal: {
      qrcode: {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
    },
    medias: {
      section: {
        iconColor: 'rgba(255, 255, 255, 0.3)',
        titleColor: 'rgba(255, 255, 255, 0.6)',
        numberColor: 'rgba(255, 255, 255, 0.15)',
      },
    },
    timeline: {
      connectorLineColor: 'rgba(255, 255, 255, 0.1)',
      titleTextColor: 'rgba(255, 255, 255, 0.9)',
      taglineTextColor: 'rgba(255, 255, 255, 0.7)',
      authorTextColor: 'rgba(255, 255, 255, 0.4)',
      timestampTextColor: 'rgba(255, 255, 255, 0.4)',
    },
  },
  debugPanel: {
    sectionTitleTextColor: 'rgba(255, 255, 255, 0.4)',
    logSeparatorColor: 'rgba(255, 255, 255, 0.05)',
    logTextColor: 'rgba(255, 255, 255, 0.6)',
    logErrorTextColor: 'rgba(244, 79, 99, 1)',
    logActionTextColor: 'rgba(48, 117, 221, 1)',
  },
  storeInfoScreen: {
    logoImageBorderColor: 'rgba(255, 255, 255, 1)',
    storeNameTextColor: 'rgba(255, 255, 255, 1)',
    orgNameTextColor: 'rgba(255, 255, 255, 0.5)',
    descriptionTextColor: 'rgba(255, 255, 255, 0.5)',
    imagePlaceholderColor: 'rgba(255, 255, 255, 0.05)',
  },
  about: {
    nameTextColor: 'rgba(255, 255, 255, 1)',
    infoLabelTextColor: 'rgba(255, 255, 255, 0.25)',
    infoTextColor: 'rgba(255, 255, 255, 0.75)',
    separatorLineColor: 'rgba(255, 255, 255, 0.05)',
    linkTextColor: 'rgba(48, 117, 221, 1)',
    descriptionTextColor: 'rgba(255, 255, 255, 0.5)',
    secondaryLinkTextColor: 'rgba(255, 255, 255, 0.35)',
    copyrightTextColor: 'rgba(255, 255, 255, 0.35)',
  },
};

export default theme;
